import { Tooltip, Typography } from 'antd';
import { formatVND } from '@/utils/format';
import { POS_IMAGE_FALLBACK } from '../utils/imagePlaceholder';
import type { PosComboSearchItem, PosVariantSearchItem } from '../types';

const { Text } = Typography;

export interface SuggestCardData {
  key: string;
  name: string;
  code: string;
  image: string;
  price: number;
  /** `null` = không áp dụng khái niệm tồn (PREORDER). */
  stock: number | null;
  isCombo: boolean;
  /** Giá gốc trước giảm — hiện gạch ngang phía trên `price` khi có (TCR-1143). */
  originalPrice?: number;
  /** Nhãn badge góc phải — mặc định "Combo" nếu `isCombo`, override khi có (TCR-1143). */
  badgeLabel?: string;
  /** Dòng phụ nhỏ dưới giá — vd hạn kết thúc flash sale (TCR-1143). */
  subLabel?: string;
}

export function variantToCard(item: PosVariantSearchItem): SuggestCardData {
  return {
    key: item.variant.variant_id,
    name: item.product.name,
    code: item.variant.variant_code,
    image: item.product.image,
    price: item.variant.unit_price ?? 0,
    // GIỮ NGUYÊN null — `?? 0` sẽ biến chế độ Đặt Trước (không có khái niệm tồn)
    // thành "hết hàng" và khoá luôn thẻ.
    stock: item.stock_quantity,
    isCombo: false,
  };
}

export function comboToCard(item: PosComboSearchItem): SuggestCardData {
  return {
    key: item.combo_id,
    name: item.combo_name,
    code: item.combo_sku,
    image: '',
    price: item.combo_price,
    stock: item.available_combo_qty,
    isCombo: true,
  };
}

/** Một thẻ trong lưới gợi ý sản phẩm POS — TCR-1126. */
export function SuggestProductCard({
  data,
  onSelect,
}: {
  data: SuggestCardData;
  onSelect: () => void;
}) {
  // `stock === null` là PREORDER (bán hàng chưa có) — không phải hết hàng.
  const soldOut = data.stock !== null && data.stock <= 0;

  return (
    <Tooltip title={data.name} mouseEnterDelay={0.4}>
      <div
        data-testid="pos-suggest-card"
        role="button"
        tabIndex={soldOut ? -1 : 0}
        aria-disabled={soldOut}
        onClick={() => {
          if (soldOut) return;
          onSelect();
        }}
        // Ngăn mousedown cướp focus khỏi ô quét mã QR — cùng pattern với
        // QRSuggestItem/QRComboSuggestItem (thu ngân dùng súng quét, mất
        // focus là quét tiếp không ăn).
        onMouseDown={(e) => e.preventDefault()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (soldOut) return;
            onSelect();
          }
        }}
        style={{
          width: 96,
          flexShrink: 0,
          border: '1px solid #e8e8e8',
          borderRadius: 6,
          padding: 4,
          cursor: soldOut ? 'not-allowed' : 'pointer',
          opacity: soldOut ? 0.45 : 1,
          background: '#fff',
        }}
      >
        <div style={{ position: 'relative' }}>
          <img
            src={data.image || POS_IMAGE_FALLBACK}
            alt={data.name}
            style={{ width: '100%', height: 68, objectFit: 'cover', borderRadius: 4 }}
            onError={(e) => {
              e.currentTarget.src = POS_IMAGE_FALLBACK;
            }}
          />
          {data.stock !== null && !data.isCombo && (
            <span
              data-testid="pos-suggest-card-stock"
              style={{
                position: 'absolute',
                top: 2,
                left: 2,
                minWidth: 18,
                padding: '0 4px',
                borderRadius: 9,
                background: soldOut ? '#8c8c8c' : '#a4193d',
                color: '#fff',
                fontSize: 11,
                lineHeight: '18px',
                textAlign: 'center',
              }}
            >
              {data.stock}
            </span>
          )}
          {(data.badgeLabel || data.isCombo) && (
            <span
              style={{
                position: 'absolute',
                top: 2,
                right: 2,
                padding: '0 4px',
                borderRadius: 3,
                background: '#a4193d',
                color: '#fff',
                fontSize: 10,
                lineHeight: '16px',
              }}
            >
              {data.badgeLabel ?? 'Combo'}
            </span>
          )}
        </div>
        <Text
          style={{ display: 'block', fontSize: 11, marginTop: 3 }}
          ellipsis={{ tooltip: false }}
        >
          {data.code}
        </Text>
        {data.originalPrice != null && data.originalPrice > data.price && (
          <Text
            delete
            style={{ display: 'block', fontSize: 10, color: '#8c8c8c' }}
          >
            {formatVND(data.originalPrice)}
          </Text>
        )}
        <Text strong style={{ display: 'block', fontSize: 11, color: '#a4193d' }}>
          {formatVND(data.price)}
        </Text>
        {data.subLabel && (
          <Text style={{ display: 'block', fontSize: 10, color: '#8c8c8c' }} ellipsis={{ tooltip: false }}>
            {data.subLabel}
          </Text>
        )}
      </div>
    </Tooltip>
  );
}
